import { onDocumentLoaded } from '@theme/utilities';

onDocumentLoaded(() => {
  /**
   * Mailing list footer behavior
   */
  const MAILING_LIST_SELECTOR = '.kinalba-mailing-list-footer';
  const MAILING_LIST_CLOSE_COOKIE = 'kinalba_newsletter_closed';
  const MAILING_LIST_OPEN_DELAY_MS = 6000; // 6 seconds
  // const MAILING_LIST_OPEN_DELAY_MS = 216000000; // DEBUG: Turning off until I can finish working on this later
  const MAILING_LIST_COOKIE_DAYS = 14; // 2 weeks
  const MAILING_LIST_SESSION_FLAG = 'kinalbaNewsletterPopupSubmitting';

  /**
   * Simple helpers for cookies
   */
  const getCookie = (name) => {
    const value = document.cookie.split('; ').find((row) => row.startsWith(`${name}=`));
    return value ? decodeURIComponent(value.split('=')[1]) : null;
  };

  const setCookie = (name, value, days) => {
    const date = new Date();
    date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
    const expires = `expires=${date.toUTCString()}`;
    document.cookie = `${name}=${encodeURIComponent(value)}; ${expires}; path=/`;
  };

  const mailingListFooter = document.querySelector(MAILING_LIST_SELECTOR);

  // Check if we're in admin.shopify.com (always show box, ignore cookies)
  const isAdminShopify = window.location.href.startsWith('https://admin.shopify.com') || 
                         window.location.href.startsWith('http://admin.shopify.com');

  // Detect successful signup via URL: ?customer_posted=true#contact_form
  let showThankYou = false;
  let thankYouUrl = false;
  try {
    const url = new URL(window.location.href);
    const customerPosted = url.searchParams.get('customer_posted') === 'true';
    const hasContactFormHash = url.hash === '#contact_form';

    thankYouUrl = customerPosted && hasContactFormHash;

    // If a customer has posted the contact form anywhere, always set the cookie
    // so we don't show the popup again.
    if (thankYouUrl) {
      setCookie(MAILING_LIST_CLOSE_COOKIE, 'true', MAILING_LIST_COOKIE_DAYS);
    }

    // Only show the thank-you message when the popup's own form was submitted
    showThankYou = thankYouUrl && sessionStorage.getItem(MAILING_LIST_SESSION_FLAG) === 'true';
  } catch {
    // Ignore URL parsing errors and fall back to normal behavior
  }

  const newsletterClosed = getCookie(MAILING_LIST_CLOSE_COOKIE);

  // Show the box if: it exists AND (we're in admin.shopify.com OR not closed OR showing thank you)
  if (mailingListFooter && (isAdminShopify || !newsletterClosed || showThankYou)) {
    // Ensure the footer is positioned for the close button
    if (!getComputedStyle(mailingListFooter).position || getComputedStyle(mailingListFooter).position === 'static') {
      mailingListFooter.style.position = 'relative';
    }

    // Create close button
    const closeButton = document.createElement('button');
    closeButton.type = 'button';
    closeButton.setAttribute('aria-label', 'Close newsletter sign up');
    closeButton.innerHTML = '×';
    Object.assign(closeButton.style, {
      position: 'absolute',
      top: '15px',
      right: '15px',
      width: '32px',
      height: '32px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'transparent',
      border: 'none',
      cursor: 'pointer',
      fontSize: '24px',
      lineHeight: '1',
      padding: '0',
      color: 'inherit',
      zIndex: '10',
    });

    const newsletterForm = mailingListFooter.querySelector('form');
    if (newsletterForm) {
      newsletterForm.addEventListener('submit', () => {
        sessionStorage.setItem(MAILING_LIST_SESSION_FLAG, 'true');
      });
    }

    mailingListFooter.appendChild(closeButton);

    // Prepare for fade-in animation
    const computedDisplay = getComputedStyle(mailingListFooter).display;
    if (computedDisplay === 'none') {
      // Reveal it but keep it invisible initially; use block by default
      mailingListFooter.style.display = 'block';
    }
    mailingListFooter.style.opacity = '0';
    // Only animate opacity so existing CSS transforms (e.g. translate) keep working
    mailingListFooter.style.transition = 'opacity 300ms ease-out';

    if (showThankYou) {
      // Successful signup via popup: show thank-you message immediately
      sessionStorage.removeItem(MAILING_LIST_SESSION_FLAG);

      // Hide any existing children except the close button
      Array.from(mailingListFooter.children).forEach((child) => {
        if (child !== closeButton) {
          /** @type {HTMLElement} */ (child).style.display = 'none';
        }
      });

      // Style the container prominently for the thank-you state
      Object.assign(mailingListFooter.style, {
        position: 'fixed',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        zIndex: '99999',
        padding: '20px',
        backgroundColor: '#ffffff',
        boxShadow: '0 4px 16px rgba(0, 0, 0, 0.15)',
        maxWidth: '90vw',
        width: 'min(500px, 90vw)',
      });

      const thankYouMessage = document.createElement('h2');
      thankYouMessage.textContent = 'Thank you for signing up.';
      thankYouMessage.style.margin = '0';
      thankYouMessage.style.paddingRight = '48px'; // avoid overlap with close button
      thankYouMessage.style.fontSize = '40px';
      mailingListFooter.appendChild(thankYouMessage);

      document.body.classList.add('kinalba-newsletter-open');
      // Use rAF to ensure transition applies
      requestAnimationFrame(() => {
        mailingListFooter.style.opacity = '1';
      });
    } else {
      // Normal behavior: open after delay
      setTimeout(() => {
        // Double‑check it still exists in DOM and wasn't closed meanwhile
        if (!document.body.contains(mailingListFooter)) return;

        document.body.classList.add('kinalba-newsletter-open');
        mailingListFooter.style.opacity = '1';
      }, MAILING_LIST_OPEN_DELAY_MS);
    }

    // Close handler
    closeButton.addEventListener('click', () => {
      setCookie(MAILING_LIST_CLOSE_COOKIE, 'true', MAILING_LIST_COOKIE_DAYS);

      // Simple fade-out
      mailingListFooter.style.opacity = '0';

      document.body.classList.remove('kinalba-newsletter-open');

      // Remove from DOM after animation
      setTimeout(() => {
        if (mailingListFooter.parentElement) {
          mailingListFooter.parentElement.removeChild(mailingListFooter);
        }
      }, 300);

      sessionStorage.removeItem(MAILING_LIST_SESSION_FLAG);
    });
  }
});